import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { CanvasSummary, VideoReference } from '@/api/types';
import { useReflowAnchor } from '@/features/video/useReflowAnchor';
import { cardAspect } from './cardAspect';
import type { CardSize, CatalogLayout } from './catalogPrefs';
import { useGridColumns } from './gridColumns';
import { distributeNatural } from './naturalColumns';
import { VideoCard } from './VideoCard';
import styles from './VideoGrid.module.css';

interface Props {
  videos: VideoReference[];
  layout: CatalogLayout;
  size: CardSize;
  bookmarks: ReadonlySet<number>;
  selected: ReadonlySet<number>;
  canvases: CanvasSummary[];
  /** Видео в панели разбора: его карточка остаётся на месте, когда панель сужает сетку. */
  openVideoId: number | null;
  /**
   * Сколько карточек показано выше этой сетки. Выдача поиска v3 рисуется двумя сетками (зоны),
   * а позиция клика и «первая карточка» считаются сквозными по всей выдаче.
   */
  indexOffset?: number;
  onOpen: (video: VideoReference, index: number) => void;
  onToggleSelect: (id: number) => void;
  onAddToCanvas: (video: VideoReference, canvasId: number, alreadyPresent: boolean) => void;
  onNewCanvas: (video: VideoReference) => void;
  onLongPress: (video: VideoReference) => void;
}

const FLY_OPEN_MS = 380;
const FLY_CLOSE_MS = 420;
/** Флайаут открывается вправо, если чипу меньше 220px до левого края экрана. */
const FLY_EDGE_PX = 220;

/** Сетка каталога: одна карточка с флайаутом за раз, ховер и таймеры живут здесь. */
export function VideoGrid({
  videos,
  layout,
  size,
  bookmarks,
  selected,
  canvases,
  openVideoId,
  indexOffset = 0,
  onOpen,
  onToggleSelect,
  onAddToCanvas,
  onNewCanvas,
  onLongPress,
}: Props) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [fly, setFly] = useState<{ id: number; dir: 'left' | 'right' } | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverOk = () => window.matchMedia('(hover: hover)').matches;

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };

  useEffect(() => () => clearTimers(), []);

  useEffect(() => {
    if (!fly) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFly(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fly]);

  const dirFor = (rect: DOMRect): 'left' | 'right' => (rect.left < FLY_EDGE_PX ? 'right' : 'left');

  const onHover = useCallback((id: number | null) => {
    setHovered(id);
    if (id === null) {
      if (openTimer.current) clearTimeout(openTimer.current);
      setFly((current) => {
        if (current && !closeTimer.current) {
          closeTimer.current = setTimeout(() => {
            closeTimer.current = null;
            setFly((f) => (f && f.id === current.id ? null : f));
          }, FLY_CLOSE_MS);
        }
        return current;
      });
    }
  }, []);

  const onFlyEnter = useCallback((id: number, rect: DOMRect) => {
    if (!hoverOk()) return;
    clearTimers();
    openTimer.current = setTimeout(() => setFly({ id, dir: dirFor(rect) }), FLY_OPEN_MS);
  }, []);

  const onFlyLeave = useCallback((id: number) => {
    if (!hoverOk()) return;
    clearTimers();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setFly((f) => (f && f.id === id ? null : f));
    }, FLY_CLOSE_MS);
  }, []);

  const onFlyToggle = useCallback((id: number, rect: DOMRect) => {
    clearTimers();
    setFly((f) => {
      if (f && f.id === id) return null;
      const dir: 'left' | 'right' =
        rect.left < FLY_EDGE_PX
          ? 'right'
          : window.innerWidth - rect.right < FLY_EDGE_PX
            ? 'left'
            : (f?.dir ?? 'left');
      return { id, dir };
    });
  }, []);

  const onFlyPick = useCallback(
    (video: VideoReference, canvasId: number, alreadyPresent: boolean) => {
      setFly(null);
      onAddToCanvas(video, canvasId, alreadyPresent);
    },
    [onAddToCanvas],
  );

  const onFlyNew = useCallback(
    (video: VideoReference) => {
      setFly(null);
      onNewCanvas(video);
    },
    [onNewCanvas],
  );

  // Панель разбора сужает сетку: колонок становится меньше, а карточка открытого видео
  // остаётся на той же высоте экрана.
  const keepCardInPlace = useReflowAnchor(gridRef, openVideoId);
  const columnCount = useGridColumns(gridRef, size, keepCardInPlace);

  // Natural раскладывается здесь, а не CSS-колонками: подгрузка страницы дописывает
  // колонки снизу и не перекладывает уже показанные карточки.
  const naturalColumns = useMemo(
    () => (layout === 'natural' ? distributeNatural(videos, columnCount) : null),
    [layout, videos, columnCount],
  );

  const card = (video: VideoReference, index: number) => (
    <VideoCard
      key={video.id}
      video={video}
      aspect={cardAspect(video)}
      isFirst={index + indexOffset === 0}
      selected={selected.has(video.id)}
      saved={bookmarks.has(video.id)}
      hovered={hovered === video.id}
      fly={fly && fly.id === video.id ? { dir: fly.dir } : null}
      canvases={canvases}
      sizeIsSmall={size === 's'}
      onHover={onHover}
      onOpen={(v) => onOpen(v, index + indexOffset)}
      onToggleSelect={onToggleSelect}
      onFlyEnter={onFlyEnter}
      onFlyLeave={onFlyLeave}
      onFlyToggle={onFlyToggle}
      onFlyPick={onFlyPick}
      onFlyNew={onFlyNew}
      onLongPress={onLongPress}
    />
  );

  return (
    <div
      ref={gridRef}
      className={styles.grid}
      data-layout={layout}
      data-size={size}
      style={{ '--grid-columns': columnCount } as CSSProperties}
    >
      {naturalColumns
        ? naturalColumns.map((indexes, column) => (
            <div
              key={column}
              className={styles.column}
              style={{ '--column-index': column } as CSSProperties}
            >
              {indexes.map((index) => card(videos[index]!, index))}
            </div>
          ))
        : videos.map((video, index) => card(video, index))}
    </div>
  );
}
